import React, { useState } from "react";
import {
  FiPlus,
  FiMinus,
  FiCheckCircle,
  FiMapPin,
  FiShoppingCart,
  FiBarChart2,
} from "react-icons/fi";
import "./sfaFaq.css";
import SfaBookingModal from "./sfaBookingModal";

const faqs = [
  {
    question: "What is TechCognics SFA?",
    answer:
      "TechCognics Sales Force Automation (SFA) is a field-sales platform designed to help businesses manage sales representatives, outlet visits, beat plans, attendance, GPS-aware field activity, order capture and sales visibility through one connected workflow.",
  },
  {
    question: "What can sales representatives do from the mobile application?",
    answer:
      "Sales representatives can manage their daily field activity from the mobile app, including attendance, planned and unscheduled visits, outlet interactions, GPS-based activity, order booking, visit outcomes and day-end activity review.",
  },
  {
    question: "Can SFA support planned and unscheduled outlet visits?",
    answer:
      "Yes. The workflow can support planned outlet coverage as well as additional unscheduled visits, allowing field teams to manage the day's assigned activity while also handling opportunities that arise during market execution.",
  },
  {
    question: "How does GPS help with field activity?",
    answer:
      "GPS provides location context during supported field activities such as attendance and outlet visits. This helps businesses maintain better visibility into where and when field execution takes place.",
  },
  {
    question: "Can sales teams capture orders during outlet visits?",
    answer:
      "Yes. Order-related activity can be connected to the field visit workflow, allowing representatives to capture sales information closer to the customer interaction and maintain a more complete record of productive visits.",
  },
  {
    question: "What happens when a visit does not result in an order?",
    answer:
      "The field workflow can capture no-order outcomes along with relevant reasons, remarks or feedback. This creates a more complete view of customer interactions instead of recording only successful orders.",
  },
  {
    question: "How does management get visibility into field performance?",
    answer:
      "Field activity captured through the SFA workflow can provide management with visibility into attendance, outlet coverage, visit activity, order-related execution and other sales indicators, helping teams review field execution more effectively.",
  },
  {
    question: "Can TechCognics SFA be used across different industries?",
    answer:
      "Yes. The platform is designed around field-sales workflows and can support businesses operating across areas such as FMCG, food and beverages, retail and distribution, pharmaceuticals, manufacturing, building materials and other field-oriented sales environments.",
  },
  {
    question: "Does TechCognics SFA replace our existing business systems?",
    answer:
      "SFA can complement broader business systems by focusing specifically on field-sales execution. The goal is to connect field activity with the wider sales and operational ecosystem rather than isolate field teams from the rest of the business.",
  },
  {
    question: "How can we get started with TechCognics SFA?",
    answer:
      "You can request a consultation with the TechCognics team to discuss your sales structure, field workflows, territories and business requirements and understand how the SFA platform can fit into your operations.",
  },
];

const SfaFaq = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSfaDemo, setShowSfaDemo] = useState(false);

  const toggleFaq = (index) => {
    setActiveIndex((previous) => (previous === index ? -1 : index));
  };

  return (
    <>
      <section className="sfa-faq-section">
        <div className="sfa-faq-orb sfa-faq-orb-left"></div>
        <div className="sfa-faq-orb sfa-faq-orb-right"></div>

        <div className="container">
          <div className="sfa-faq-layout">
            <div className="sfa-faq-intro" data-aos="fade-right">
              <span className="sfa-faq-kicker">
                <span className="sfa-faq-kicker-dot"></span>
                FAQ
              </span>

              <h2>
                Questions About
                <span> SFA?</span>
              </h2>

              <p>
                Everything you need to know about bringing field-sales
                execution, outlet visits, order capture and management
                visibility together in one connected workflow.
              </p>

              <div className="sfa-faq-summary">
                <div className="sfa-faq-summary-item">
                  <div className="sfa-faq-summary-icon teal">
                    <FiMapPin />
                  </div>

                  <div>
                    <strong>Field Execution</strong>
                    <span>Plan, visit & manage outlets</span>
                  </div>
                </div>

                <div className="sfa-faq-summary-item">
                  <div className="sfa-faq-summary-icon orange">
                    <FiShoppingCart />
                  </div>

                  <div>
                    <strong>Sales Activity</strong>
                    <span>Capture orders from the field</span>
                  </div>
                </div>

                <div className="sfa-faq-summary-item">
                  <div className="sfa-faq-summary-icon cyan">
                    <FiBarChart2 />
                  </div>

                  <div>
                    <strong>Management Visibility</strong>
                    <span>Understand field performance</span>
                  </div>
                </div>
              </div>

              <div className="sfa-faq-cta">
                <div className="sfa-faq-cta-icon">
                  <FiCheckCircle />
                </div>

                <div className="sfa-faq-cta-content">
                  <strong>Still have questions?</strong>

                  <span>Talk to our SFA team about your requirements.</span>

                  <button type="button" onClick={()=>setShowSfaDemo(true)}>
                    Talk to an Expert
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>

            <div
              className="sfa-faq-list"
              data-aos="fade-left"
              data-aos-delay="120"
            >
              {faqs.map((faq, index) => {
                const isOpen = activeIndex === index;

                return (
                  <div
                    className={`sfa-faq-item ${
                      isOpen ? "sfa-faq-item-open" : ""
                    }`}
                    key={faq.question}
                  >
                    <button
                      type="button"
                      className="sfa-faq-question"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={isOpen}
                      aria-controls={`sfa-faq-answer-${index}`}
                    >
                      <span className="sfa-faq-question-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span className="sfa-faq-question-text">
                        {faq.question}
                      </span>

                      <span className="sfa-faq-toggle">
                        {isOpen ? <FiMinus /> : <FiPlus />}
                      </span>
                    </button>

                    <div
                      id={`sfa-faq-answer-${index}`}
                      className="sfa-faq-answer"
                      aria-hidden={!isOpen}
                    >
                      <div className="sfa-faq-answer-inner">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <SfaBookingModal
        show={showSfaDemo}
        onClose={() => setShowSfaDemo(false)}
      />
    </>
  );
};

export default SfaFaq;
